import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Button, Modal, Zap } from '@opendate-app/setlist';
import { toastError, toastSuccess } from '../../../shared/toastHelper';
import {
  holdChallengeErrorMessage,
  startHoldChallenge,
  startedChallengeDescription,
  withdrawHoldChallenge
} from '../api/holdChallengesApi';
import '../styles/holdChallenges.css';

const NO_HOLD_CHALLENGES = {
  canStartChallenges: false,
  startChallenge: () => {},
  startingHoldPositionId: null,
  highlightedHoldPositionIds: []
};

const MobileHoldChallengesContext = createContext(NO_HOLD_CHALLENGES);

export const useMobileHoldChallenges = () => useContext(MobileHoldChallengesContext);

const isActiveHold = (event) => event.type === 'Hold' && Boolean(event.clearable);

export const hasActiveHoldAhead = (dayEvents, hold) => (
  dayEvents.some((other) => (
    other.hold_position_id !== hold.hold_position_id
      && isActiveHold(other)
      && (other.room_ids || []).some((roomId) => (hold.room_ids || []).includes(roomId))
      && other.start_date_iso === hold.start_date_iso
      && other.position < hold.position
  ))
);

export const canChallengeHold = (dayEvents, hold) => (
  hold.authorized
    && isActiveHold(hold)
    && !(hold.challenges || []).some((challenge) => challenge.role === 'challenger')
    && hasActiveHoldAhead(dayEvents, hold)
);

export const ChallengeStartedModal = ({ challenge, team, onClose, onWithdrawn }) => {
  const [withdrawing, setWithdrawing] = useState(false);

  const withdraw = () => {
    setWithdrawing(true);
    withdrawHoldChallenge(team, challenge.id)
      .then(() => {
        toastSuccess('Challenge withdrawn');
        onWithdrawn();
        onClose();
      })
      .catch((error) => {
        toastError(holdChallengeErrorMessage(error, 'Could not withdraw the challenge.'));
        setWithdrawing(false);
      });
  };

  return (
    <Modal
      open
      onClose={onClose}
      icon={false}
      title='Challenge started'
      description={startedChallengeDescription(challenge)}
      closeOnEsc={!withdrawing}
      showClose={!withdrawing}
      footer={(
        <>
          <Button variant='secondary' onClick={withdraw} disabled={withdrawing}>
            {withdrawing ? 'Withdrawing…' : 'Withdraw'}
          </Button>
          <Button variant='primary' onClick={onClose} disabled={withdrawing}>
            Done
          </Button>
        </>
      )}
    />
  );
};

ChallengeStartedModal.propTypes = {
  challenge: PropTypes.shape({
    id: PropTypes.number.isRequired,
    respond_by: PropTypes.string.isRequired,
    challenger: PropTypes.shape({ position: PropTypes.number, title: PropTypes.string }).isRequired,
    challenged: PropTypes.shape({ position: PropTypes.number, title: PropTypes.string }).isRequired
  }).isRequired,
  team: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onWithdrawn: PropTypes.func.isRequired
};

export const MobileHoldChallengesProvider = ({
  team,
  canManageHoldChallenges,
  highlightedHoldPositionIds,
  onChallengesChanged,
  children
}) => {
  const [startingHoldPositionId, setStartingHoldPositionId] = useState(null);
  const [startedChallenge, setStartedChallenge] = useState(null);

  const startChallenge = useCallback((hold) => {
    setStartingHoldPositionId(hold.hold_position_id);
    startHoldChallenge(team, hold.hold_position_id)
      .then((challenge) => {
        if (challenge.challenged) setStartedChallenge(challenge);
        else toastSuccess('Challenge started');
      })
      .catch((error) => toastError(holdChallengeErrorMessage(error, 'Could not start the challenge.')))
      .finally(() => {
        setStartingHoldPositionId(null);
        onChallengesChanged();
      });
  }, [team, onChallengesChanged]);

  const value = useMemo(() => ({
    canStartChallenges: canManageHoldChallenges,
    startChallenge,
    startingHoldPositionId,
    highlightedHoldPositionIds
  }), [canManageHoldChallenges, startChallenge, startingHoldPositionId, highlightedHoldPositionIds]);

  return (
    <MobileHoldChallengesContext.Provider value={value}>
      {children}
      {startedChallenge && (
        <ChallengeStartedModal
          challenge={startedChallenge}
          team={team}
          onClose={() => setStartedChallenge(null)}
          onWithdrawn={onChallengesChanged}
        />
      )}
    </MobileHoldChallengesContext.Provider>
  );
};

MobileHoldChallengesProvider.propTypes = {
  team: PropTypes.object.isRequired,
  canManageHoldChallenges: PropTypes.bool.isRequired,
  highlightedHoldPositionIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChallengesChanged: PropTypes.func.isRequired,
  children: PropTypes.node
};

export const MobileHoldChallengeButton = ({ hold, dayEvents, stacked }) => {
  const { canStartChallenges, startChallenge, startingHoldPositionId } = useMobileHoldChallenges();

  if (!canStartChallenges || !canChallengeHold(dayEvents, hold)) return null;

  return (
    <span className={`mobile-hold-challenge-action${stacked ? ' mobile-hold-challenge-action--stacked' : ''}`}>
      <Button
        variant='secondary'
        size='sm'
        icon={Zap}
        disabled={startingHoldPositionId !== null}
        aria-label={`Challenge the hold ahead of H${hold.position}`}
        onClick={() => startChallenge(hold)}
      >
        Challenge
      </Button>
    </span>
  );
};

MobileHoldChallengeButton.propTypes = {
  hold: PropTypes.shape({
    hold_position_id: PropTypes.number,
    position: PropTypes.number,
    challenges: PropTypes.array
  }).isRequired,
  dayEvents: PropTypes.array.isRequired,
  stacked: PropTypes.bool
};

MobileHoldChallengeButton.defaultProps = {
  stacked: false
};
